/**
 * SLA Compliance Report
 * Track project delivery against SLA targets
 * Task: Phase 4 - IT Companies SLA Monitoring
 */

import { useState, useMemo } from 'react';
import { IndustryProject, ProjectStatus } from '@/types/industry';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { format, differenceInDays, parseISO, isAfter, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SLAComplianceReportProps {
  projects: IndustryProject[];
  clientMap?: Record<number, string>;
  onExportPDF?: () => void;
  onExportCSV?: () => void;
}

type PeriodFilter = 'all' | 'current_month' | 'last_month' | 'current_quarter' | 'current_year';
type ComplianceFilter = 'all' | 'on_time' | 'at_risk' | 'delayed';

interface ProjectSLAMetrics {
  project: IndustryProject;
  slaDeadline: Date | null;
  actualCompletion: Date | null;
  daysToDeadline: number;
  daysDelayed: number;
  complianceStatus: 'on_time' | 'at_risk' | 'delayed' | 'pending';
  compliancePercentage: number;
}

export function SLAComplianceReport({
  projects,
  clientMap = {},
  onExportPDF,
  onExportCSV,
}: SLAComplianceReportProps) {
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [complianceFilter, setComplianceFilter] = useState<ComplianceFilter>('all');

  // Calculate SLA metrics for each project
  const projectSLAMetrics = useMemo(() => {
    const now = new Date();

    return projects.map((project): ProjectSLAMetrics => {
      // SLA deadline from metadata or calculated from start_date + estimated duration
      const slaDeadlineStr = (project.metadata?.slaDeadline as string) || project.expected_end_date;
      const slaDeadline = slaDeadlineStr ? parseISO(slaDeadlineStr) : null;

      const actualCompletion = project.end_date ? parseISO(project.end_date) : null;
      const projectStart = project.start_date ? parseISO(project.start_date) : null;

      let daysToDeadline = 0;
      let daysDelayed = 0;
      let complianceStatus: ProjectSLAMetrics['complianceStatus'] = 'pending';
      let compliancePercentage = 100;

      if (slaDeadline) {
        if (actualCompletion) {
          // Project completed
          daysDelayed = differenceInDays(actualCompletion, slaDeadline);
          if (daysDelayed <= 0) {
            complianceStatus = 'on_time';
            compliancePercentage = 100;
          } else {
            complianceStatus = 'delayed';
            compliancePercentage = Math.max(0, 100 - (daysDelayed / 7) * 10); // -10% per week delayed
          }
        } else if (project.status === 'in_progress' || project.status === 'planned') {
          // Project in progress or planned
          daysToDeadline = differenceInDays(slaDeadline, now);

          if (daysToDeadline < 0) {
            complianceStatus = 'delayed';
            daysDelayed = Math.abs(daysToDeadline);
            compliancePercentage = Math.max(0, 100 - (daysDelayed / 7) * 10);
          } else if (daysToDeadline <= 3) {
            complianceStatus = 'at_risk';
            compliancePercentage = 80;
          } else {
            complianceStatus = 'on_time';
            compliancePercentage = 100;
          }
        }
      }

      return {
        project,
        slaDeadline,
        actualCompletion,
        daysToDeadline,
        daysDelayed,
        complianceStatus,
        compliancePercentage,
      };
    });
  }, [projects]);

  // Filter by period
  const filteredByPeriod = useMemo(() => {
    if (periodFilter === 'all') return projectSLAMetrics;

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return projectSLAMetrics.filter(({ project }) => {
      if (!project.start_date) return false;
      const startDate = new Date(project.start_date);

      switch (periodFilter) {
        case 'current_month':
          return startDate.getMonth() === currentMonth && startDate.getFullYear() === currentYear;
        case 'last_month':
          const lastMonth = new Date(currentYear, currentMonth - 1);
          return startDate.getMonth() === lastMonth.getMonth() && startDate.getFullYear() === lastMonth.getFullYear();
        case 'current_quarter':
          const quarter = Math.floor(currentMonth / 3);
          const startQuarterMonth = quarter * 3;
          return (
            startDate.getMonth() >= startQuarterMonth &&
            startDate.getMonth() < startQuarterMonth + 3 &&
            startDate.getFullYear() === currentYear
          );
        case 'current_year':
          return startDate.getFullYear() === currentYear;
        default:
          return true;
      }
    });
  }, [projectSLAMetrics, periodFilter]);

  // Filter by compliance
  const filteredMetrics = useMemo(() => {
    if (complianceFilter === 'all') return filteredByPeriod;
    return filteredByPeriod.filter(({ complianceStatus }) => complianceStatus === complianceFilter);
  }, [filteredByPeriod, complianceFilter]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const total = filteredMetrics.length;
    const onTime = filteredMetrics.filter(m => m.complianceStatus === 'on_time').length;
    const atRisk = filteredMetrics.filter(m => m.complianceStatus === 'at_risk').length;
    const delayed = filteredMetrics.filter(m => m.complianceStatus === 'delayed').length;
    const pending = filteredMetrics.filter(m => m.complianceStatus === 'pending').length;

    const avgCompliance = total > 0
      ? filteredMetrics.reduce((sum, m) => sum + m.compliancePercentage, 0) / total
      : 100;

    const totalDelayDays = filteredMetrics.reduce((sum, m) => sum + m.daysDelayed, 0);
    const avgDelay = delayed > 0 ? totalDelayDays / delayed : 0;

    return {
      total,
      onTime,
      atRisk,
      delayed,
      pending,
      avgCompliance,
      avgDelay,
      complianceRate: total > 0 ? (onTime / total) * 100 : 0,
    };
  }, [filteredMetrics]);

  // Format date
  const formatDate = (date: Date | null) => {
    if (!date) return '-';
    return format(date, 'dd/MM/yyyy', { locale: ptBR });
  };

  // Get compliance badge variant
  const getComplianceBadgeVariant = (status: ProjectSLAMetrics['complianceStatus']) => {
    switch (status) {
      case 'on_time':
        return 'default';
      case 'at_risk':
        return 'secondary';
      case 'delayed':
        return 'destructive';
      case 'pending':
        return 'outline';
    }
  };

  // Get compliance label
  const getComplianceLabel = (status: ProjectSLAMetrics['complianceStatus']) => {
    switch (status) {
      case 'on_time':
        return 'No Prazo';
      case 'at_risk':
        return 'Em Risco';
      case 'delayed':
        return 'Atrasado';
      case 'pending':
        return 'Pendente';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Relatório de SLA</h2>
          <p className="text-muted-foreground">Cumprimento de prazos e compromissos</p>
        </div>
        <div className="flex gap-2">
          {onExportCSV && (
            <Button variant="outline" size="sm" onClick={onExportCSV}>
              <FileText className="mr-2 h-4 w-4" />
              Exportar CSV
            </Button>
          )}
          {onExportPDF && (
            <Button variant="outline" size="sm" onClick={onExportPDF}>
              <Download className="mr-2 h-4 w-4" />
              Exportar PDF
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select value={periodFilter} onValueChange={(val) => setPeriodFilter(val as PeriodFilter)}>
          <SelectTrigger>
            <SelectValue placeholder="Filtrar por período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os períodos</SelectItem>
            <SelectItem value="current_month">Mês atual</SelectItem>
            <SelectItem value="last_month">Mês passado</SelectItem>
            <SelectItem value="current_quarter">Trimestre atual</SelectItem>
            <SelectItem value="current_year">Ano atual</SelectItem>
          </SelectContent>
        </Select>

        <Select value={complianceFilter} onValueChange={(val) => setComplianceFilter(val as ComplianceFilter)}>
          <SelectTrigger>
            <SelectValue placeholder="Filtrar por compliance" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="on_time">No Prazo</SelectItem>
            <SelectItem value="at_risk">Em Risco</SelectItem>
            <SelectItem value="delayed">Atrasados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Taxa de Compliance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.complianceRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.onTime} de {stats.total} projetos no prazo
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Projetos no Prazo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.onTime}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Entregues dentro do SLA
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Em Risco / Atrasados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{stats.atRisk + stats.delayed}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.atRisk} em risco · {stats.delayed} atrasados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Atraso Médio</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.avgDelay.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Dias de atraso (quando ocorre)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Projects Table */}
      <Card>
        <CardHeader>
          <CardTitle>Projetos ({filteredMetrics.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Projeto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Prazo SLA</TableHead>
                  <TableHead>Data Conclusão</TableHead>
                  <TableHead className="text-center">Dias Restantes/Atraso</TableHead>
                  <TableHead className="text-center">Compliance</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMetrics.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Nenhum projeto encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMetrics.map(({
                    project,
                    slaDeadline,
                    actualCompletion,
                    daysToDeadline,
                    daysDelayed,
                    complianceStatus,
                    compliancePercentage
                  }) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{project.name}</div>
                          {project.start_date && (
                            <div className="text-xs text-muted-foreground">
                              Início: {format(parseISO(project.start_date), 'dd/MM/yyyy', { locale: ptBR })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {project.client_id ? (
                          <span className="text-sm">{clientMap[project.client_id] || `Cliente ${project.client_id}`}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(slaDeadline)}</TableCell>
                      <TableCell>{formatDate(actualCompletion)}</TableCell>
                      <TableCell className="text-center">
                        {complianceStatus === 'delayed' ? (
                          <span className="text-red-600 font-medium">+{daysDelayed} dias</span>
                        ) : complianceStatus === 'at_risk' ? (
                          <span className="text-orange-600 font-medium">{daysToDeadline} dias</span>
                        ) : complianceStatus === 'on_time' && !actualCompletion ? (
                          <span className="text-green-600">{daysToDeadline} dias</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">{compliancePercentage.toFixed(0)}%</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={getComplianceBadgeVariant(complianceStatus)}>
                          {getComplianceLabel(complianceStatus)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
